"use client"

import React, { useState } from "react"

interface TabItem {
  id: string
  label: string
  content: React.ReactNode
  isDisabled?: boolean
}

interface TabsProps {
  tabs: TabItem[]
  defaultTab?: string
  className?: string
  tabClassName?: string
  activeTabClassName?: string
  inactiveTabClassName?: string
  contentClassName?: string
  activeTab?: string
  setActiveTab?: (tabId: string) => void
}

export function Tabs({
  tabs,
  defaultTab,
  className,
  tabClassName,
  activeTabClassName,
  inactiveTabClassName,
  contentClassName,
  activeTab, setActiveTab,
}: TabsProps) {

  return (
    <div className={`w-full ${className || ""}`}>
      <div className="flex">
        {tabs.map((tab, tabIndex = 0) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab?.(tab.id)}
            disabled={tab.isDisabled}
            className={`pb-2 px-4 ${
              tabIndex === 0 ? "" : "ml-3"
            } disabled:cursor-not-allowed text-subTitle transition-colors relative ${
              tabClassName || ""
            } ${
              activeTab === tab.id
                ? `text-p_primary border-b-[2px] border-p_primary ${
                    activeTabClassName || ""
                  }`
                : `text-secondary_text disabled:text-secondary_text border-b-[2px] border-secondary_text ${
                    inactiveTabClassName || ""
                  }`
            }`}
            data-testid={tab.label.toLowerCase().replace(/ /g, "_")}
          >
            {tab.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={`inline-flex items-center justify-center rounded-md p-1 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 ${className || ""}`}
    {...props}
  />
))
TabsList.displayName = "TabsList"

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(({ className, children, ...props }, ref) => (
  <button
    ref={ref}
    className={`inline-flex items-center justify-center px-3 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 data-[state=active]:bg-blue-500 data-[state=active]:text-white ${className || ""}`}
    {...props}
  >
    {children}
  </button>
))
TabsTrigger.displayName = "TabsTrigger"

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {}

const TabsContent = React.forwardRef<HTMLDivElement, TabsContentProps>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={`mt-4 p-4 focus:outline-none focus:ring-2 focus:ring-blue-500 ${className || ""}`}
    {...props}
  >
    {children}
  </div>
))
TabsContent.displayName = "TabsContent"

export { TabsList, TabsTrigger, TabsContent }
